import React, { useEffect, useState } from "react";
import { api, type MaintenanceRequest, type VisitRequest, type VacateRequest } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { RequestDetail } from "../shared/RequestDetail";

const VACATE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const VISIT_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const MAINTENANCE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const REQUEST_STATUS_KEY: Record<string, string> = {
  open: "ownerRequests.statusOpen",
  in_progress: "ownerRequests.statusInProgress",
  resolved: "ownerRequests.statusResolved",
};

const VISIT_STATUS_KEY: Record<string, string> = {
  pending: "ownerRequests.visitStatusPending",
  confirmed: "ownerRequests.visitStatusConfirmed",
  declined: "ownerRequests.visitStatusDeclined",
  cancelled: "ownerRequests.visitStatusCancelled",
};

const VACATE_STATUS_KEY: Record<string, string> = {
  pending: "ownerRequests.vacateStatusPending",
  acknowledged: "ownerRequests.vacateStatusAcknowledged",
  cancelled: "ownerRequests.vacateStatusCancelled",
};

function fmtDate(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const VALID_TABS = new Set(["vacate", "visits", "maintenance"]);

export function OwnerRequests({
  focusTab,
  onVisitsChanged,
}: { focusTab?: string | null; onVisitsChanged?: () => void } = {}) {
  const [requests, setRequests] = useState<MaintenanceRequest[] | null>(null);
  const [visits, setVisits] = useState<VisitRequest[] | null>(null);
  const [vacateRequests, setVacateRequests] = useState<VacateRequest[] | null>(null);
  const [openRequestId, setOpenRequestId] = useState<string | null>(null);
  // A notification deep link (e.g. a visit request) opens straight to the
  // tab it's about instead of always landing on Vacate.
  const [tab, setTab] = useState<"vacate" | "visits" | "maintenance">(
    () => (focusTab && VALID_TABS.has(focusTab) ? (focusTab as any) : "vacate")
  );
  const { t } = useLanguage();

  async function load() {
    const [{ requests }, { visits }, { vacateRequests }] = await Promise.all([
      api.get("/api/requests/owner"),
      api.get("/api/visits/owner"),
      api.get("/api/vacate-requests/owner"),
    ]);
    // Urgent, unresolved requests first — otherwise keep the server's
    // newest-first order.
    requests.sort((a: MaintenanceRequest, b: MaintenanceRequest) => {
      const aUrgent = a.urgency === "urgent" && a.status !== "resolved";
      const bUrgent = b.urgency === "urgent" && b.status !== "resolved";
      return aUrgent === bUrgent ? 0 : aUrgent ? -1 : 1;
    });
    setRequests(requests);
    setVisits(visits);
    setVacateRequests(vacateRequests);
  }

  useEffect(() => {
    load();
  }, []);

  if (!requests || !visits || !vacateRequests) return <div className="spinner" />;

  if (openRequestId) {
    return <RequestDetail requestId={openRequestId} onBack={() => setOpenRequestId(null)} onChanged={load} />;
  }

  const pendingVisits = visits.filter((v) => v.status === "pending");
  const decidedVisits = visits.filter((v) => v.status !== "pending");
  const pendingVacates = vacateRequests.filter((v) => v.status === "pending");
  const decidedVacates = vacateRequests.filter((v) => v.status !== "pending");

  return (
    <>
      <div className="seg-control">
        <button className={`seg-btn${tab === "vacate" ? " active" : ""}`} onClick={() => setTab("vacate")}>
          {t("ownerRequests.vacateTab")}
          {pendingVacates.length > 0 && <span className="seg-count">{pendingVacates.length}</span>}
        </button>
        <button className={`seg-btn${tab === "visits" ? " active" : ""}`} onClick={() => setTab("visits")}>
          {t("ownerRequests.visitsTab")}
          {pendingVisits.length > 0 && <span className="seg-count">{pendingVisits.length}</span>}
        </button>
        <button className={`seg-btn${tab === "maintenance" ? " active" : ""}`} onClick={() => setTab("maintenance")}>
          {t("ownerRequests.maintenanceTab")}
          {requests.filter((r) => r.status !== "resolved").length > 0 && (
            <span className="seg-count">{requests.filter((r) => r.status !== "resolved").length}</span>
          )}
        </button>
      </div>

      {tab === "vacate" && (
        <>
          {vacateRequests.length === 0 && <div className="empty-state">{t("ownerRequests.emptyNoVacates")}</div>}
          {pendingVacates.map((v) => (
            <VacateCard key={v.id} v={v} onChanged={load} />
          ))}
          {decidedVacates.map((v) => (
            <VacateCard key={v.id} v={v} onChanged={load} />
          ))}
        </>
      )}

      {tab === "visits" && (
        <>
          {visits.length === 0 && <div className="empty-state">{t("ownerRequests.emptyNoVisits")}</div>}
          {pendingVisits.map((v) => (
            <VisitCard key={v.id} v={v} onChanged={() => { load(); onVisitsChanged?.(); }} />
          ))}
          {decidedVisits.map((v) => (
            <VisitCard key={v.id} v={v} onChanged={() => { load(); onVisitsChanged?.(); }} />
          ))}
        </>
      )}

      {tab === "maintenance" && (
        <>
          {requests.length === 0 && <div className="empty-state">{t("ownerRequests.emptyNoRequests")}</div>}
          {requests.map((r) => (
            <div className="req-card maintenance" key={r.id} onClick={() => setOpenRequestId(r.id)} style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div className="req-icon maintenance">{MAINTENANCE_ICON}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontSize: 13.5 }}>{r.title}</b>
                  <div className="sub">{r.description || t("ownerRequests.noDetailsProvided")}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                {r.urgency === "urgent" && r.status !== "resolved" && <span className="badge pending">{t("requests.urgentBadge")}</span>}
                <span className={`badge ${r.status}`}>{t(REQUEST_STATUS_KEY[r.status] ?? r.status)}</span>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}

function VisitCard({ v, onChanged }: { v: VisitRequest; onChanged: () => void }) {
  const toast = useToast();
  const { t } = useLanguage();

  async function decide(status: "confirmed" | "declined") {
    await api.patch(`/api/visits/${v.id}`, { status });
    toast(status === "confirmed" ? t("ownerRequests.visitConfirmed") : t("ownerRequests.visitDeclined"));
    onChanged();
  }

  const dateLabel = new Date(v.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="req-card visit">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div className="req-icon visit">{VISIT_ICON}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <b style={{ fontSize: 13.5 }}>{v.tenantName}</b>
            {v.tenantVerified && (
              <span style={{ color: "var(--success)", fontWeight: 800, fontSize: 11 }} title={t("ownerRequests.verifiedTenantBadge")}>
                &#10003; {t("ownerRequests.verifiedTenantBadge")}
              </span>
            )}
          </div>
          <div className="sub">{v.tenantPhone || t("ownerRequests.noPhoneOnFile")}</div>
        </div>
        <span className={`badge ${v.status === "confirmed" ? "paid" : v.status === "declined" || v.status === "cancelled" ? "pending" : "vacant"}`}>
          {t(VISIT_STATUS_KEY[v.status] ?? v.status)}
        </span>
      </div>
      <div className="sub" style={{ marginTop: 8 }}>
        {t("ownerRequests.visitLine", { property: v.propertyLabel ?? "", date: dateLabel, time: v.time ?? "" })}
      </div>
      {v.note && <div className="sub" style={{ marginTop: 4 }}>&ldquo;{v.note}&rdquo;</div>}
      {v.status === "pending" && (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => decide("declined")}>
            {t("ownerRequests.declineButton")}
          </button>
          <button className="btn-solid btn-sm" style={{ flex: 1 }} onClick={() => decide("confirmed")}>
            {t("ownerRequests.confirmButton")}
          </button>
        </div>
      )}
    </div>
  );
}

function VacateCard({ v, onChanged }: { v: VacateRequest; onChanged: () => void }) {
  const toast = useToast();
  const { t } = useLanguage();

  async function acknowledge() {
    await api.patch(`/api/vacate-requests/${v.id}`, { status: "acknowledged" });
    toast(t("ownerRequests.vacateAcknowledged"));
    onChanged();
  }

  return (
    <div className="req-card vacate">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div className="req-icon vacate">{VACATE_ICON}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <b style={{ fontSize: 13.5 }}>{v.tenantName}</b>
          <div className="sub">{v.tenantPhone || t("ownerRequests.noPhoneOnFile")}</div>
        </div>
        <span className={`badge ${v.status === "acknowledged" ? "paid" : v.status === "cancelled" ? "vacant" : "pending"}`}>
          {t(VACATE_STATUS_KEY[v.status] ?? v.status)}
        </span>
      </div>
      <div className="sub" style={{ marginTop: 8 }}>
        {t("ownerRequests.vacateLine", { property: v.propertyLabel ?? "", date: fmtDate(v.vacateDate) })}
      </div>
      <div className="sub" style={{ marginTop: 2 }}>
        {t("ownerRequests.noticeGivenLine", { date: fmtDate(v.noticeDate) })}
      </div>
      {v.note && <div className="sub" style={{ marginTop: 4 }}>&ldquo;{v.note}&rdquo;</div>}
      {v.status === "pending" && (
        <button className="btn-solid btn-sm" style={{ marginTop: 10 }} onClick={acknowledge}>
          {t("ownerRequests.acknowledgeButton")}
        </button>
      )}
    </div>
  );
}
